import errorToast from "../helper-components/error-toast";

// Batch fields in the order they appear in the batch panel, so the first
// missing field the user sees in the toast is the left-most one on screen.
const BATCH_FIELD_ORDER = [
  "item_batch_number",
  "item_exp_date",
  "item_sellable_quantity",
  "item_batch_free_quantity",
  "item_batch_purchase_rate",
  "item_batch_unit_price",
];

const collectSchemaErrors = (schema, values) => {
  const errors = {};
  if (!schema) return errors;
  try {
    schema.validateSync(values, { abortEarly: false });
  } catch (err) {
    (err?.inner?.length ? err.inner : [err]).forEach((e) => {
      if (e?.path && !errors[e.path]) errors[e.path] = e.message;
    });
  }
  return errors;
};

// Validates a purchase draft (Purchase Invoice / GRN) top-down - vendor,
// address, items, then each row's batches - and toasts only the first
// problem found. Returns true when the draft can be submitted.
export const validatePurchaseDraft = (values, schema, prefix) => {
  const errors = collectSchemaErrors(schema, values);

  const firstError = (paths) => paths.map((p) => errors[p]).find(Boolean);

  // 1) Vendor
  const vendorError = firstError(["cmr_code", "cmr_name"]);
  if (vendorError) {
    errorToast(vendorError);
    return false;
  }

  // 2) Local purchase - on behalf of
  if (errors.on_behalf_of_name) {
    errorToast(errors.on_behalf_of_name);
    return false;
  }

  // 3) Address
  if (
    !values?.[`${prefix}_pay_to_address`] ||
    !values?.[`${prefix}_ship_to_address`]
  ) {
    errorToast("Please verify and save the address");
    return false;
  }

  // 4) Line items (last row is always the blank entry row)
  const items = values?.data?.slice(0, -1) || [];
  if (items.length === 0) {
    errorToast("Please add at least one item");
    return false;
  }

  // 5) Batch level, row by row
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const itemLabel = item?.item_name || item?.item_code || `Item ${i + 1}`;
    const batches = item?.item_batches || [];
    for (let b = 0; b < batches.length; b++) {
      const base = `data[${i}].item_batches[${b}]`;
      const batchError = firstError(
        BATCH_FIELD_ORDER.map((field) => `${base}.${field}`)
      );
      const otherError = Object.keys(errors).find((p) =>
        p.startsWith(`${base}.`)
      );
      if (batchError || otherError) {
        errorToast(
          `Row ${i + 1} (${itemLabel}), Batch ${b + 1}: ${
            batchError || errors[otherError]
          }`
        );
        return false;
      }
    }
  }

  // 6) Anything else the schema rejects (footer / other fields)
  const remaining = Object.keys(errors).find(
    (p) => !p.startsWith(`data[${items.length}]`)
  );
  if (remaining) {
    errorToast(errors[remaining]);
    return false;
  }

  return true;
};
